import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TagSelector } from '@/components/TagSelector';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';

interface BulkTagDialogProps {
  open: boolean;
  onClose: () => void;
  photoIds: string[];
  organizationId: string;
  onSuccess?: () => void;
}

export function BulkTagDialog({ open, onClose, photoIds, organizationId, onSuccess }: BulkTagDialogProps) {
  const [selectedTags, setSelectedTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalOperations, setTotalOperations] = useState(0);
  const [completedOperations, setCompletedOperations] = useState(0);

  useEffect(() => {
    if (open && organizationId) {
      loadAvailableTags();
    }
  }, [open, organizationId]);

  const loadAvailableTags = async () => {
    const { data: tagsData } = await supabase
      .from('tags')
      .select('id, name, color')
      .eq('organization_id', organizationId)
      .order('name');
    
    if (tagsData) {
      setAvailableTags(tagsData);
    }
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      toast.error('Please add at least one tag');
      return;
    }

    setIsSubmitting(true);
    setProgress(0);
    setCompletedOperations(0);

    try {
      // Create all possible photo-tag combinations
      const photoTagEntries = photoIds.flatMap(photoId =>
        selectedTags.map(tag => ({
          photo_id: photoId,
          tag_id: tag.id,
        }))
      );

      setTotalOperations(photoTagEntries.length);
      setProgress(10);

      // Check which combinations already exist
      const { data: existingTags } = await supabase
        .from('photo_tags')
        .select('photo_id, tag_id')
        .in('photo_id', photoIds)
        .in('tag_id', selectedTags.map(t => t.id));

      setProgress(30);

      // Filter out existing combinations
      const existingSet = new Set(
        (existingTags || []).map(et => `${et.photo_id}-${et.tag_id}`)
      );
      
      const newEntries = photoTagEntries.filter(
        entry => !existingSet.has(`${entry.photo_id}-${entry.tag_id}`)
      );

      setProgress(50);

      let insertedCount = 0;
      
      // Only insert if there are new entries
      if (newEntries.length > 0) {
        // Process in batches for better progress tracking
        const batchSize = 50;
        for (let i = 0; i < newEntries.length; i += batchSize) {
          const batch = newEntries.slice(i, i + batchSize);
          
          const { data, error: photoTagsError } = await supabase
            .from('photo_tags')
            .insert(batch)
            .select();

          if (photoTagsError) {
            throw photoTagsError;
          }

          insertedCount += data?.length || 0;
          setCompletedOperations(insertedCount);
          setProgress(50 + (insertedCount / newEntries.length) * 50);
        }
      } else {
        setProgress(100);
      }

      const duplicatesCount = photoTagEntries.length - newEntries.length;
      
      if (duplicatesCount > 0 && insertedCount > 0) {
        toast.success(`Added ${insertedCount} tag associations (${duplicatesCount} already existed)`);
      } else if (insertedCount > 0) {
        toast.success(`Added ${selectedTags.length} tag(s) to ${photoIds.length} photo(s)`);
      } else {
        toast.info('All selected tags were already associated with these photos');
      }
      
      setSelectedTags([]);
      
      // Small delay to show 100% completion, then refresh and close
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error adding tags:', error);
      toast.error('Failed to add tags');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isSubmitting ? undefined : onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSubmitting && progress === 100 ? 'Tagging Complete' : 'Add Tags to Photos'}
          </DialogTitle>
          {!isSubmitting && (
            <DialogDescription>
              Select or create tags to add to {photoIds.length} selected photos
            </DialogDescription>
          )}
        </DialogHeader>

        {isSubmitting ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium">
                  Processing {totalOperations} operations
                </span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {completedOperations} complete
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-auto p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
            <Progress value={progress} className="w-full h-2" />
          </div>
        ) : (
          <div className="space-y-4">
            <TagSelector
              trigger={
                <Button variant="outline" className="w-full justify-start">
                  <Plus className="w-4 h-4 mr-2" />
                  {selectedTags.length > 0 
                    ? `${selectedTags.length} tag(s) selected` 
                    : 'Select tags'}
                </Button>
              }
              selectedTags={selectedTags}
              availableTags={availableTags}
              organizationId={organizationId}
              onTagsChange={setSelectedTags}
              onTagCreated={loadAvailableTags}
              align="start"
            />

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={selectedTags.length === 0}>
                Add Tags
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
